import React, { useState, useEffect } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import ResourceCard from './ResourceCard';
import { 
  uploadResource, 
  getProjectResources, 
  deleteResource, 
  getResourcesByType
} from '../services/resourceService';

const ResourceManager = ({ projectId, userType = 'company' }) => {
  const { currentUser } = React.useContext(AuthContext);
  
  // State management
  const [resources, setResources] = useState([]);
  const [filteredResources, setFilteredResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [filter, setFilter] = useState('all');
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    resourceType: 'document'
  });
  const [file, setFile] = useState(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [error, setError] = useState('');

  // Load resources on mount
  useEffect(() => {
    loadResources();
  }, [projectId]);

  // Filter resources when filter changes
  useEffect(() => {
    filterResources();
  }, [resources, filter]);

  const loadResources = async () => {
    try {
      setLoading(true);
      const resourceData = await getProjectResources(projectId);
      setResources(resourceData);
    } catch (error) {
      console.error('Error loading resources:', error);
      setError('Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  const filterResources = () => {
    if (filter === 'all') {
      setFilteredResources(resources);
    } else {
      setFilteredResources(resources.filter(r => r.resourceType === filter));
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.title.trim()) {
      setError('Please enter a title');
      return;
    }

    if (formData.resourceType === 'link' && !linkUrl.trim()) {
      setError('Please enter a valid URL');
      return;
    }

    if (formData.resourceType !== 'link' && !file) {
      setError('Please select a file');
      return;
    }

    try {
      setUploading(true);
      
      const resourceData = {
        title: formData.title,
        description: formData.description,
        resourceType: formData.resourceType,
        uploadedBy: currentUser.displayName || 'Unknown',
        url: formData.resourceType === 'link' ? linkUrl : undefined
      };

      const resourceId = await uploadResource(
        projectId, 
        currentUser.uid, 
        resourceData, 
        formData.resourceType === 'link' ? null : file
      );

      // Reset form
      setFormData({
        title: '',
        description: '',
        resourceType: 'document'
      });
      setFile(null);
      setLinkUrl('');
      setShowUploadForm(false);

      // Reload resources
      await loadResources();

    } catch (error) {
      console.error('Error uploading resource:', error);
      setError(error.message || 'Failed to upload resource');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (resourceId) => {
    try {
      await deleteResource(resourceId, projectId, currentUser.uid);
      await loadResources();
    } catch (error) {
      console.error('Error deleting resource:', error);
      throw error;
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file size (10MB limit)
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      setFile(selectedFile);
      setError('');
    }
  };

  const getAcceptedFileTypes = () => {
    switch (formData.resourceType) {
      case 'document':
        return '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.rtf';
      case 'video':
        return 'video/*';
      case 'image':
        return 'image/*';
      default:
        return '*';
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <div style={{ fontSize: '18px', color: '#666' }}>Loading resources...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Filter and upload controls */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <span style={{ fontSize: '16px', fontWeight: '500' }}>Filter:</span>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              fontSize: '14px'
            }}
          >
            <option value="all">All Resources ({resources.length})</option>
            <option value="document">Documents</option>
            <option value="video">Videos</option>
            <option value="link">Links</option>
          </select>
        </div>

        {userType === 'company' && (
          <button
            onClick={() => setShowUploadForm(!showUploadForm)}
            className="btn btn-primary"
            style={{ padding: '10px 20px' }}
          >
            {showUploadForm ? 'Cancel' : '+ Add Resource'}
          </button>
        )}
      </div>

      {/* Upload form */}
      {showUploadForm && userType === 'company' && (
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '16px',
          border: '1px solid #e5e7eb',
          marginBottom: '24px'
        }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600' }}>
            Add New Resource
          </h3>

          {error && (
            <div style={{
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              color: '#dc2626',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleUpload}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Resource Type
              </label>
              <select
                value={formData.resourceType}
                onChange={(e) => setFormData({ ...formData, resourceType: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db'
                }}
              >
                <option value="document">Document</option>
                <option value="video">Video</option>
                <option value="link">External Link</option>
              </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Project Requirements, Tutorial Video, Reference Documentation"
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db'
                }}
                required
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Description (Optional)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Provide additional context about this resource"
                rows="3"
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  resize: 'vertical'
                }}
              />
            </div>

            {formData.resourceType === 'link' ? (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  URL
                </label>
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://example.com"
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #d1d5db'
                  }}
                  required
                />
              </div>
            ) : (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  File
                </label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  accept={getAcceptedFileTypes()}
                  style={{ width: '100%', padding: '8px 0' }}
                  required
                />
                <p style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                  Maximum file size: 10MB. 
                  {formData.resourceType === 'document' && ' Accepted: PDF, Word, Excel, PowerPoint, Text files'}
                  {formData.resourceType === 'video' && ' Accepted: MP4, MOV, AVI, etc.'}
                </p>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="submit"
                disabled={uploading}
                className="btn btn-primary"
                style={{ minWidth: '120px' }}
              >
                {uploading ? 'Uploading...' : 'Upload Resource'}
              </button>
              <button
                type="button"
                onClick={() => setShowUploadForm(false)}
                className="btn btn-outline"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Resources grid */}
      {filteredResources.length === 0 ? (
        <div style={{
          backgroundColor: '#f9fafb',
          padding: '60px 32px',
          borderRadius: '16px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '20px', marginBottom: '20px', color: '#6b7280', fontWeight: '600' }}>No resources</div>
          <h3 style={{
            fontSize: '20px',
            fontWeight: 'bold',
            color: '#1a1a1a',
            marginBottom: '12px'
          }}>
            {filter === 'all' ? 'No resources available' : `No ${filter}s found`}
          </h3>
          <p style={{
            color: '#666',
            fontSize: '16px',
            margin: 0
          }}>
            {userType === 'company' 
              ? 'Upload your first resource to get started.' 
              : 'Resources will appear here when they become available.'
            }
          </p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '20px'
        }}>
          {filteredResources.map((resource) => (
            <ResourceCard
              key={resource.id}
              resource={resource}
              currentUser={currentUser}
              onDelete={userType === 'company' ? handleDelete : undefined}
              showDeleteButton={userType === 'company'}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ResourceManager; 